import React from "react";

/**
 * The portal's icon set.
 *
 * One geometric family — 24px grid, 1.7 stroke, round caps and joins, no fills
 * and no colour of its own (everything inherits `currentColor`). Drawn here
 * rather than pulled from a pack so every glyph shares the same optical weight
 * and nothing emoji-flavoured can creep in. Add new glyphs to this file so the
 * family stays consistent.
 */

const Svg = ({ size = 18, strokeWidth = 1.7, children, ...rest }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
    style={{ flexShrink: 0, display: "block" }}
    {...rest}
  >
    {children}
  </svg>
);

/* --- Brand ------------------------------------------------------------- */

/** The app mark: a stylised open book / stacked pages. */
export const LogoMark = ({ size = 20, ...p }) => (
  <Svg size={size} strokeWidth={1.9} {...p}>
    <path d="M4 5.5A1.5 1.5 0 0 1 5.5 4H10a2.5 2.5 0 0 1 2 1v13a2.5 2.5 0 0 0-2-1H5.5A1.5 1.5 0 0 1 4 15.5Z" />
    <path d="M20 5.5A1.5 1.5 0 0 0 18.5 4H14a2.5 2.5 0 0 0-2 1v13a2.5 2.5 0 0 1 2-1h4.5a1.5 1.5 0 0 0 1.5-1.5Z" />
  </Svg>
);

/* --- Navigation & chrome ------------------------------------------------ */

export const ChevronLeft = (p) => (
  <Svg {...p}>
    <path d="M14.5 5.5 8 12l6.5 6.5" />
  </Svg>
);

export const ChevronRight = (p) => (
  <Svg {...p}>
    <path d="M9.5 5.5 16 12l-6.5 6.5" />
  </Svg>
);

export const ChevronDown = (p) => (
  <Svg {...p}>
    <path d="M5.5 9 12 15.5 18.5 9" />
  </Svg>
);

export const ArrowRight = (p) => (
  <Svg {...p}>
    <path d="M4.5 12h15" />
    <path d="M13.5 6 19.5 12l-6 6" />
  </Svg>
);

export const ArrowLeft = (p) => (
  <Svg {...p}>
    <path d="M19.5 12h-15" />
    <path d="M10.5 6 4.5 12l6 6" />
  </Svg>
);

export const Logout = (p) => (
  <Svg {...p}>
    <path d="M14 20H6.5A1.5 1.5 0 0 1 5 18.5v-13A1.5 1.5 0 0 1 6.5 4H14" />
    <path d="M15.5 8.5 19 12l-3.5 3.5" />
    <path d="M19 12H9.5" />
  </Svg>
);

export const ExternalLink = (p) => (
  <Svg {...p}>
    <path d="M13.5 5H19v5.5" />
    <path d="M19 5l-7.5 7.5" />
    <path d="M18 14.5v4A1.5 1.5 0 0 1 16.5 20h-11A1.5 1.5 0 0 1 4 18.5v-11A1.5 1.5 0 0 1 5.5 6h4" />
  </Svg>
);

/* --- Content types ------------------------------------------------------ */

/** Slide deck / presentation. */
export const Slides = (p) => (
  <Svg {...p}>
    <rect x="3.25" y="4.25" width="17.5" height="12" rx="1.75" />
    <path d="M12 16.25V20" />
    <path d="M9 20h6" />
    <path d="M7.5 12.5V9" />
    <path d="M12 12.5V7.5" />
    <path d="M16.5 12.5v-2" />
  </Svg>
);

/** Video lesson. */
export const Video = (p) => (
  <Svg {...p}>
    <rect x="3.25" y="5.25" width="12.5" height="13.5" rx="2" />
    <path d="M15.75 10.5 20.75 7.5v9l-5-3z" />
  </Svg>
);

/** Play — the resume affordance. */
export const Play = (p) => (
  <Svg {...p}>
    <path d="M7.5 5.5 18.5 12l-11 6.5z" />
  </Svg>
);

/** Document / PDF. */
export const Document = (p) => (
  <Svg {...p}>
    <path d="M13.5 3.5H7A1.5 1.5 0 0 0 5.5 5v14A1.5 1.5 0 0 0 7 20.5h10a1.5 1.5 0 0 0 1.5-1.5V8.5z" />
    <path d="M13.5 3.5v5h5" />
    <path d="M9 13h6" />
    <path d="M9 16.5h4" />
  </Svg>
);

/** A single lesson within a module. */
export const Lesson = (p) => (
  <Svg {...p}>
    <path d="M4.5 5.5A1.5 1.5 0 0 1 6 4h9.5L19.5 8v10.5a1.5 1.5 0 0 1-1.5 1.5H6a1.5 1.5 0 0 1-1.5-1.5z" />
    <path d="M15 4v4.5h4.5" />
    <path d="M8.5 12.5h7" />
    <path d="M8.5 16h4.5" />
  </Svg>
);

/** A module — a bound set of lessons. */
export const Module = (p) => (
  <Svg {...p}>
    <path d="M4 5.75A1.75 1.75 0 0 1 5.75 4H18a1.5 1.5 0 0 1 1.5 1.5v11H6.25A2.25 2.25 0 0 0 4 18.75z" />
    <path d="M4 18.75A2.25 2.25 0 0 1 6.25 16.5H19.5V20H6.25A2.25 2.25 0 0 1 4 18.75z" />
    <path d="M8 8.5h7" />
  </Svg>
);

/** Warm up / practice questions. */
export const Quiz = (p) => (
  <Svg {...p}>
    <circle cx="12" cy="12" r="8.5" />
    <path d="M9.75 9.5a2.3 2.3 0 0 1 4.4.9c0 1.55-2.15 1.9-2.15 3.35" />
    <path d="M12 17h.01" strokeWidth="2.1" />
  </Svg>
);

/** Formal assessment. */
export const Assessment = (p) => (
  <Svg {...p}>
    <path d="M8 4.5H6.5A1.5 1.5 0 0 0 5 6v13a1.5 1.5 0 0 0 1.5 1.5h11A1.5 1.5 0 0 0 19 19V6a1.5 1.5 0 0 0-1.5-1.5H16" />
    <rect x="8" y="2.75" width="8" height="3.5" rx="1.1" />
    <path d="M8.75 12.25l2 2 3.75-4" />
  </Svg>
);

/** SCORM package. */
export const Package = (p) => (
  <Svg {...p}>
    <path d="M12 3.5 20 7.75v8.5L12 20.5 4 16.25v-8.5z" />
    <path d="M4 7.75 12 12l8-4.25" />
    <path d="M12 12v8.5" />
  </Svg>
);

/** A learning path — ordered, branching route. */
export const Path = (p) => (
  <Svg {...p}>
    <circle cx="6.5" cy="6" r="2.5" />
    <circle cx="17.5" cy="18" r="2.5" />
    <path d="M6.5 8.5v3.75A2.75 2.75 0 0 0 9.25 15h5.5A2.75 2.75 0 0 1 17.5 17.75v-.25" />
  </Svg>
);

/* --- Status & feedback -------------------------------------------------- */

export const Check = (p) => (
  <Svg strokeWidth={2.4} {...p}>
    <path d="M5 12.5 9.5 17 19 7" />
  </Svg>
);

export const CheckCircle = (p) => (
  <Svg {...p}>
    <circle cx="12" cy="12" r="8.5" />
    <path d="M8.5 12.25 11 14.75l4.5-5" strokeWidth="2" />
  </Svg>
);

export const Clock = (p) => (
  <Svg {...p}>
    <circle cx="12" cy="12" r="8.5" />
    <path d="M12 7.25V12l3.25 1.9" />
  </Svg>
);

export const Calendar = (p) => (
  <Svg {...p}>
    <rect x="3.75" y="5.5" width="16.5" height="14.75" rx="1.75" />
    <path d="M3.75 10h16.5" />
    <path d="M8.25 3.5v4" />
    <path d="M15.75 3.5v4" />
  </Svg>
);

/** Session / login count. */
export const Sessions = (p) => (
  <Svg {...p}>
    <path d="M10 4H6.5A1.5 1.5 0 0 0 5 5.5v13A1.5 1.5 0 0 0 6.5 20H10" />
    <path d="M14.5 8.5 18 12l-3.5 3.5" />
    <path d="M18 12H8.5" />
  </Svg>
);

/** Award / certificate — a laurel-free medal, deliberately plain. */
export const Award = (p) => (
  <Svg {...p}>
    <circle cx="12" cy="9" r="5.25" />
    <path d="M8.6 13.3 7.25 20.5 12 17.9l4.75 2.6-1.35-7.2" />
  </Svg>
);

/** Achievements / standings. */
export const Trophy = (p) => (
  <Svg {...p}>
    <path d="M7.5 4h9v5a4.5 4.5 0 0 1-9 0z" />
    <path d="M7.5 5.5H5a2 2 0 0 0 2.5 3.4" />
    <path d="M16.5 5.5H19a2 2 0 0 1-2.5 3.4" />
    <path d="M12 13.5V17" />
    <path d="M8.75 20h6.5" />
    <path d="M10 17h4l.75 3h-5.5z" />
  </Svg>
);

/* --- Data & sections ---------------------------------------------------- */

export const BarChart = (p) => (
  <Svg {...p}>
    <path d="M4 20h16" />
    <path d="M7.25 20V11" />
    <path d="M12 20V5" />
    <path d="M16.75 20v-6" />
  </Svg>
);

export const Grid = (p) => (
  <Svg {...p}>
    <rect x="4" y="4" width="7" height="7" rx="1.5" />
    <rect x="13" y="4" width="7" height="7" rx="1.5" />
    <rect x="4" y="13" width="7" height="7" rx="1.5" />
    <rect x="13" y="13" width="7" height="7" rx="1.5" />
  </Svg>
);

export const Globe = (p) => (
  <Svg {...p}>
    <circle cx="12" cy="12" r="8.5" />
    <path d="M3.5 12h17" />
    <path d="M12 3.5a13 13 0 0 1 0 17 13 13 0 0 1 0-17z" />
  </Svg>
);

/** Support / lifebuoy. */
export const Support = (p) => (
  <Svg {...p}>
    <circle cx="12" cy="12" r="8.5" />
    <circle cx="12" cy="12" r="3.5" />
    <path d="M14.5 9.5 18 6" />
    <path d="M9.5 9.5 6 6" />
    <path d="M14.5 14.5 18 18" />
    <path d="M9.5 14.5 6 18" />
  </Svg>
);

/** Sales / upward trend. */
export const Trend = (p) => (
  <Svg {...p}>
    <path d="M4 16.5 9.5 11l3.5 3.5L20 7.5" />
    <path d="M15 7.5h5v5" />
  </Svg>
);

/** Enterprise system / building blocks. */
export const System = (p) => (
  <Svg {...p}>
    <rect x="3.75" y="3.75" width="7" height="7" rx="1.5" />
    <rect x="13.25" y="3.75" width="7" height="7" rx="1.5" />
    <rect x="3.75" y="13.25" width="7" height="7" rx="1.5" />
    <path d="M16.75 13.25v7" />
    <path d="M13.25 16.75h7" />
  </Svg>
);

/** Knowledge base / FAQ. */
export const Help = (p) => (
  <Svg {...p}>
    <path d="M4.5 6A1.5 1.5 0 0 1 6 4.5h12A1.5 1.5 0 0 1 19.5 6v9.5a1.5 1.5 0 0 1-1.5 1.5H9l-4.5 3.25z" />
    <path d="M10 9.25a2.15 2.15 0 0 1 4.1.85c0 1.45-2 1.75-2 3.15" />
    <path d="M12 15.5h.01" strokeWidth="2.1" />
  </Svg>
);

/** Empty inbox. */
export const Inbox = (p) => (
  <Svg {...p}>
    <path d="M3.75 13.5 6.5 5.25A1.5 1.5 0 0 1 7.9 4.25h8.2a1.5 1.5 0 0 1 1.4 1L20.25 13.5v4.75A1.5 1.5 0 0 1 18.75 19.75H5.25a1.5 1.5 0 0 1-1.5-1.5z" />
    <path d="M3.75 13.5h4.5l1.25 2.25h5l1.25-2.25h4.5" />
  </Svg>
);

export const Download = (p) => (
  <Svg {...p}>
    <path d="M12 4v10.5" />
    <path d="M7.75 10.75 12 15l4.25-4.25" />
    <path d="M4.5 18.5h15" />
  </Svg>
);

export const Search = (p) => (
  <Svg {...p}>
    <circle cx="11" cy="11" r="6.75" />
    <path d="M15.9 15.9 20 20" />
  </Svg>
);

export const Maximise = (p) => (
  <Svg {...p}>
    <path d="M9 4.5H4.5V9" />
    <path d="M15 4.5h4.5V9" />
    <path d="M15 19.5h4.5V15" />
    <path d="M9 19.5H4.5V15" />
  </Svg>
);

/**
 * Section name → glyph. Sections are configured server-side, so this maps the
 * known ones by name and falls back to a neutral "system" mark rather than
 * guessing — an unknown section still renders correctly, just generically.
 */
const SECTION_ICONS = {
  erp: System,
  "lms dc": Slides,
  faq: Help,
  sales: Trend,
  support: Support,
  intranet: Globe,
  "zoho crm": Grid,
};

export const sectionIcon = (name) =>
  SECTION_ICONS[String(name || "").trim().toLowerCase()] || Module;

/** Content type → glyph, shared by every list that shows mixed content. */
const CONTENT_ICONS = {
  "ppt-module": Module,
  "ppt-item": Lesson,
  video: Video,
  assessment: Assessment,
  "learning-path": Path,
  scorm: Package,
};

export const contentIcon = (contentType) => CONTENT_ICONS[contentType] || Lesson;
